import { Store } from '../../../store/index';
import {
    resetCallkitData,
    setMakeCallDetails,
    initializeParticipants,
    updateParticipantStatus,
    setCallScreenStatus,
    setCallMode,
    addParticipantToCall,
} from '../model';
import SDK from '../../../Components/SDK';
import {
    CALL_MODES,
    CALL_TYPE,
    CALLKIT_CALLSCREENSTATUS,
    CALLKIT_CALLSTATUS,
    NEGOTIATION_TIMED_OUT,
    NOT_ABLE_TO_ANSWER,
    NOT_ABLE_TO_MUTE_AUDIO,
    NOT_ABLE_TO_MUTE_VIDEO,
    NOT_ABLE_TO_UNMUTE_AUDIO,
    NOT_ABLE_TO_UNMUTE_VIDEO,
    SOMETHING_WENT_WRONG,
    AUDIO_CALL_PERMISSION_DENIED_CODE,
    VIDEO_CALL_PERMISSION_DENIED_CODE,
} from '../constants';
import { blockOfflineAction } from '../../../Helpers/Utility';
import { locale as i18n } from '../../../Helpers/Locale/Index';
import {
    isUserIBlocked as getIsBlockedByMe,
    isUserWhoBlockedMe as getIsBlockedMe,
} from '../../../Helpers/Chat/BlockContact';
import logger from '../../../Helpers/logger';
import { getLocalUserName, handleCallError, isAlreadyInCall, isOwnUserReconnecting } from './helper';
import { showInfoToast as showErrorToast } from './toastMessage';

export const makeOneToOneCallKitAudioCall = activeChatId => {
    if (blockOfflineAction() || isOwnUserReconnecting()) return;
    if (isAlreadyInCall()) return;
    if (getIsBlockedByMe(activeChatId) || getIsBlockedMe(activeChatId)) {
        showErrorToast(i18n.t('calls.NOT_ALLOWED_TO_MAKECALL'));
        return;
    }
    const currentUserId = getLocalUserName();
    const participants = [
        {
            userId: currentUserId,
            callStatus: CALLKIT_CALLSTATUS.CONNECTED,
            isAudioMuted: false,
            isVideoMuted: true,
            stream: null,
        },
        {
            userId: activeChatId,
            callStatus: CALLKIT_CALLSTATUS.TRY_TO_CONNECT,
            isAudioMuted: false,
            isVideoMuted: true,
            stream: null,
        },
    ];
    Store.dispatch(initializeParticipants(participants));
    SDK.makeCall({ calleeId: [activeChatId], isMicrophoneEnabled: true, isVideoCall: false }, (success, err) => {
        if (err) {
            logger.log('Err', err);
            if (!handleCallError(err)) {
                showErrorToast(err?.message || SOMETHING_WENT_WRONG);
            }
            Store.dispatch(resetCallkitData());
        } else {
            Store.dispatch(
                setMakeCallDetails({
                    calleeId: [activeChatId],
                    isMicrophoneEnabled: true,
                    isVideoCall: false,
                    callStatus: CALLKIT_CALLSTATUS.TRY_TO_CONNECT,
                    callMode: CALL_MODES.ONETOONE,
                    callType: CALL_TYPE.AUDIO,
                }),
            );
        }
    });
};

export const makeOneToOneCallKitVideoCall = activeChatId => {
    if (blockOfflineAction() || isOwnUserReconnecting()) return;
    if (isAlreadyInCall()) return;
    if (getIsBlockedByMe(activeChatId) || getIsBlockedMe(activeChatId)) {
        showErrorToast(i18n.t('calls.NOT_ALLOWED_TO_MAKECALL'));
        return;
    }
    const currentUserId = getLocalUserName();
    const participants = [
        {
            userId: currentUserId,
            callStatus: CALLKIT_CALLSTATUS.CONNECTED,
            isAudioMuted: false,
            isVideoMuted: false,
            stream: null,
        },
        {
            userId: activeChatId,
            callStatus: CALLKIT_CALLSTATUS.TRY_TO_CONNECT,
            isAudioMuted: false,
            isVideoMuted: false,
            stream: null,
        },
    ];
    Store.dispatch(initializeParticipants(participants));
    SDK.makeCall({ calleeId: [activeChatId], isMicrophoneEnabled: true, isVideoCall: true }, (success, err) => {
        if (err) {
            logger.log('Err', err);
            if (!handleCallError(err)) {
                showErrorToast(err?.message || SOMETHING_WENT_WRONG);
            }
            Store.dispatch(resetCallkitData());
        } else {
            Store.dispatch(
                setMakeCallDetails({
                    calleeId: [activeChatId],
                    isMicrophoneEnabled: true,
                    isVideoCall: true,
                    callStatus: CALLKIT_CALLSTATUS.TRY_TO_CONNECT,
                    callMode: CALL_MODES.ONETOONE,
                    callType: CALL_TYPE.VIDEO,
                }),
            );
        }
    });
};

export const makeGroupCallKitCall = (calleeId, groupId, isVideoCall, groupName) => {
    if (blockOfflineAction() || isOwnUserReconnecting()) return;
    if (isAlreadyInCall()) return;
    const filteredCalleeId = calleeId
        .filter(id => !getIsBlockedByMe(id) && !getIsBlockedMe(id))
        .map(id => (id.includes('@') ? id.split('@')[0] : id));
    if (calleeId.length > 0 && filteredCalleeId.length === 0) {
        showErrorToast(i18n.t('calls.NOT_ALLOWED_TO_MAKECALL'));
        return;
    }

    const currentUserId = getLocalUserName();
    const participants = filteredCalleeId.map(id => ({
        userId: id,
        callStatus: CALLKIT_CALLSTATUS.TRY_TO_CONNECT,
        isAudioMuted: false,
        isVideoMuted: true,
        stream: null,
    }));
    participants.push({
        userId: currentUserId,
        callStatus: CALLKIT_CALLSTATUS.CONNECTED,
        isAudioMuted: false,
        isVideoMuted: !isVideoCall,
        stream: null,
    });
    Store.dispatch(initializeParticipants(participants));

    // Resolve groupName from Redux groups store if not explicitly passed
    const resolvedGroupName =
        groupName ||
        (() => {
            const rawGroupId = groupId ? groupId.split('@')[0] : null;
            const groups = Store.getState().groupsData?.data || [];
            const matched = groups.find(g => g.groupId === rawGroupId || g.groupId === groupId);
            return matched?.groupName || matched?.name || '';
        })();

    Store.dispatch(
        setMakeCallDetails({
            calleeId: filteredCalleeId,
            groupId,
            groupName: resolvedGroupName,
            isMicrophoneEnabled: true,
            isVideoCall,
            callStatus: CALLKIT_CALLSTATUS.TRY_TO_CONNECT,
            callMode: CALL_MODES.ONETOMANY,
            callType: isVideoCall ? CALL_TYPE.VIDEO : CALL_TYPE.AUDIO,
        }),
    );

    SDK.makeGroupCall(
        { calleeId: filteredCalleeId, groupId, isMicrophoneEnabled: true, isVideoCall },
        (success, err) => {
            logger.debug('groupId', groupId);
            if (err) {
                logger.log(err);
                Store.dispatch(resetCallkitData());
                if (
                    err.statusCode === AUDIO_CALL_PERMISSION_DENIED_CODE ||
                    err.statusCode === VIDEO_CALL_PERMISSION_DENIED_CODE
                ) {
                    if (!handleCallError(err)) {
                        showErrorToast(err.message);
                    }
                }
            } else {
                if (success?.participants && Array.isArray(success.participants)) {
                    success.participants.forEach(participant => {
                        Store.dispatch(
                            updateParticipantStatus({
                                userId: participant.identity,
                                callStatus: participant.callStatus,
                                ...participant,
                            }),
                        );
                    });
                }
            }
        },
    );
};

export const attendCallKitCall = () => {
    if (blockOfflineAction()) return;
    SDK.connectCall((success, err) => {
        if (err) {
            logger.log(err);
            if (err.message !== NEGOTIATION_TIMED_OUT) {
                if (!handleCallError(err)) {
                    showErrorToast(err?.message || NOT_ABLE_TO_ANSWER);
                }
            }
            rejectCallKitCall();
        } else {
            logger.log(success);
            Store.dispatch(setCallScreenStatus(CALLKIT_CALLSCREENSTATUS.CONNECTED));
        }
    });
};

export const endCallKitCall = callback => {
    Store.dispatch(resetCallkitData());
    SDK.disconnectCall((success, err) => {
        if (err) {
            logger.log(err);
        } else {
            logger.log(success);
        }
        if (typeof callback === 'function') {
            callback(success, err);
        }
    });
};

export const rejectCallKitCall = () => {
    Store.dispatch(resetCallkitData());
    SDK.rejectCall((success, err) => {
        if (err) {
            logger.log(err);
        } else {
            logger.log(success);
        }
    });
};

/**
 * Accept the second incoming call; the SDK puts the ongoing one on hold.
 */
export const acceptWaitingCallKitCall = () => {
    SDK.acceptWaitingCall((success, err) => {
        if (err) {
            if (!handleCallError(err)) {
                showErrorToast(err?.message);
            }
            return;
        }
        logger.log(success);
    });
};

/**
 * Decline the second incoming call and stay on the ongoing one.
 */
export const declineWaitingCallKitCall = () => {
    SDK.declineWaitingCall((success, err) => {
        if (err) {
            logger.log(err);
        } else {
            logger.log(success);
        }
    });
};

export const toggleCallKitAudio = isAudioMuted => {
    if (isOwnUserReconnecting() || blockOfflineAction()) return;
    const newMuted = !isAudioMuted;
    const enable = !newMuted;
    SDK.setMicrophoneEnable(enable, (success, err) => {
        if (err) {
            if (!handleCallError(err)) {
                showErrorToast(err?.message || (enable ? NOT_ABLE_TO_UNMUTE_AUDIO : NOT_ABLE_TO_MUTE_AUDIO));
            }
        } else {
            const currentUserId = getLocalUserName();
            Store.dispatch(updateParticipantStatus({ userId: currentUserId, isAudioMuted: newMuted }));
        }
    });
};

export const toggleCallKitVideo = isVideoMuted => {
    if (isOwnUserReconnecting() || blockOfflineAction()) return;
    const newMuted = !isVideoMuted;
    const enable = !newMuted;
    SDK.setCameraEnable(enable, (success, err) => {
        if (err) {
            showErrorToast(err?.message || (enable ? NOT_ABLE_TO_UNMUTE_VIDEO : NOT_ABLE_TO_MUTE_VIDEO));
        } else {
            const currentUserId = getLocalUserName();
            Store.dispatch(updateParticipantStatus({ userId: currentUserId, isVideoMuted: newMuted }));
        }
    });
};

export const switchVideoCall = (status, callback) => {
    if (isOwnUserReconnecting()) return;
    SDK.videoCallSwitch(status, callback);
};

export const inviteUsersToCallKit = userList => {
    if (isOwnUserReconnecting() || blockOfflineAction()) return;
    const filteredUserList = userList
        .filter(userId => !getIsBlockedByMe(userId) && !getIsBlockedMe(userId))
        .map(userId => (userId.includes('@') ? userId.split('@')[0] : userId));

    if (userList.length > 0 && filteredUserList.length === 0) {
        showErrorToast(i18n.t('calls.NOT_ALLOWED_TO_MAKECALL'));
        return;
    }
    SDK.inviteUsersToOngoingCall({ inviteUserList: filteredUserList }, (success, err) => {
        if (err) {
            showErrorToast(err?.message || SOMETHING_WENT_WRONG);
            logger.log(err);
        } else {
            logger.log('Invite success', success);
            if (success?.participants?.length > 0) {
                Store.dispatch(setCallMode(CALL_MODES.ONETOMANY));
                success.participants.forEach(user => {
                    Store.dispatch(
                        addParticipantToCall({
                            userId: user.identity,
                            callStatus: user.callStatus,
                            ...user,
                        }),
                    );
                });
            }
        }
    });
};

export const subscribeCallEvent = (serverLink, callback) => {
    SDK.subscribeCallEvent({ callLink: serverLink }, (success, err) => {
        if (err) {
            logger.log('err', err);
        }
        if (success) {
            logger.log('success', success);
        }
        callback(success, err);
    });
};

export const unSubscribeCallEvent = callback => {
    SDK.unSubscribeCallEvent((success, err) => {
        if (err) {
            logger.log('err', err);
        }
        if (success) {
            logger.log('success', success);
        }
        callback(success, err);
    });
};

export const joinCallEvent = (audioMute, videoMute, callback) => {
    if (blockOfflineAction()) return;
    SDK.JoinLink({ isMicrophoneEnable: !audioMute, isCameraEnable: !videoMute }, (success, err) => {
        if (err) {
            logger.log('err', err);
            handleCallError(err);
        }
        if (success) {
            logger.log('success', success);
        }
        callback(success, err);
    });
};

export const getCallLogs = ({ lastSyncTime, direction, size }, callback) => {
    SDK.getCallLog({ lastSyncTime, direction, size }, (success, err) => {
        if (err) {
            logger.debug('err', err);
        }
        if (success) {
            logger.debug('success', success);
        }
        callback(success, err);
    });
};

export const deleteCallLogs = ({ callIds, clearAll }, callback) => {
    SDK.deleteCallLog({ callIds, clearAll }, (success, err) => {
        if (err) {
            logger.debug('err', err);
            showErrorToast(SOMETHING_WENT_WRONG);
        }
        if (success) {
            logger.debug('success', success);
        }
        callback(success, err);
    });
};

export const seenCallLogs = ({ callIds, markAll }, callback) => {
    SDK.callSeen({ callIds, markAll }, (success, err) => {
        if (err) {
            logger.debug('err', err);
        }
        if (success) {
            logger.debug('success', success);
        }
        callback(success, err);
    });
};

export const scheduleMeet = ({ toJid, msgId, title, link, scheduledDateTime }) => {
    if (blockOfflineAction()) return;
    const response = SDK.sendMeetMessage({
        toJid,
        msgId,
        title,
        link,
        scheduledDateTime,
    });
    return response;
};

export const startScreenCapture = callback => {
    if (blockOfflineAction()) return;
    SDK.startScreenCapture((success, err) => {
        if (err) {
            logger.debug('err', err);
        }
        if (success) {
            logger.debug('success', success);
        }
        callback(success, err);
    });
};

export const stopScreenCapture = callback => {
    if (blockOfflineAction()) return;
    SDK.stopScreenCapture((success, err) => {
        if (err) {
            logger.debug('err', err);
        }
        if (success) {
            logger.debug('success', success);
        }
        callback(success, err);
    });
};
